import { CategoryTree } from '@/components/category-tree';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { type FilterOptions } from '@/types';
import { ChevronDown, ChevronRight, ChevronUp, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export interface BoatFilters {
    category: string[];
    condition: 'new' | 'used' | 'all';
    location: string[];
    yearRange: [number, number];
    priceRange: [number, number];
    hours: [number, number];
}

interface AppliedFilter {
    type: keyof BoatFilters;
    value: string | number;
    display: string;
}

interface FilterSectionProps {
    title: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

type SectionKey = 'category' | 'condition' | 'location' | 'year' | 'price' | 'hours';
type RangeBound = 'min' | 'max';

const MOBILE_COLLAPSED_SECTIONS: Record<SectionKey, boolean> = {
    category: false,
    condition: false,
    location: false,
    year: false,
    price: false,
    hours: false,
};

const DESKTOP_DEFAULT_SECTIONS: Record<SectionKey, boolean> = {
    category: true,
    condition: true,
    location: false,
    year: false,
    price: false,
    hours: false,
};

const MOBILE_BREAKPOINT = 1024;
const DEFAULT_HOURS_RANGE: [number, number] = [0, 10000];

const getInitialExpandedState = () => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
        return { ...MOBILE_COLLAPSED_SECTIONS };
    }

    return { ...DESKTOP_DEFAULT_SECTIONS };
};

const clampNumber = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const normalizeRange = (value: [number, number], min: number, max: number): [number, number] => {
    const start = clampNumber(value[0], min, max);
    const end = clampNumber(value[1], min, max);

    return [Math.min(start, end), Math.max(start, end)];
};

const parseNullableNumber = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) {
        return null;
    }

    const parsed = Number(trimmed);

    if (!Number.isFinite(parsed)) {
        return null;
    }

    return parsed;
};

const stateLabel = (name: string, code: string) => `${name}, ${code}`;

const toRangeValue = (value: number[], fallback: [number, number]): [number, number] => {
    if (value.length < 2) {
        return fallback;
    }

    return [value[0] ?? fallback[0], value[1] ?? fallback[1]];
};

const FilterSection: React.FC<FilterSectionProps> = ({ title, expanded, onToggle, children }) => {
    return (
        <section className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="flex w-full items-center justify-between py-2 text-left font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
                {title}
                {expanded ? <ChevronDown aria-hidden="true" className="h-4 w-4" /> : <ChevronRight aria-hidden="true" className="h-4 w-4" />}
            </button>

            {expanded && <div className="mt-3 space-y-3">{children}</div>}
        </section>
    );
};

interface BoatFilterSidebarProps {
    filters: BoatFilters;
    onFiltersChange: (filters: BoatFilters) => void;
    onSaveSearch: () => void;
    filterOptions: FilterOptions;
    className?: string;
}

export const BoatFilterSidebar: React.FC<BoatFilterSidebarProps> = ({ filters, onFiltersChange, onSaveSearch, filterOptions, className = '' }) => {
    const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>(getInitialExpandedState);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

    const yearMin = filterOptions?.yearRange?.min ?? 1990;
    const yearMax = filterOptions?.yearRange?.max ?? new Date().getFullYear();
    const priceMin = filterOptions?.priceRange?.min ?? 0;
    const priceMax = filterOptions?.priceRange?.max ?? 1000000;
    const hoursMin = DEFAULT_HOURS_RANGE[0];
    const hoursMax = DEFAULT_HOURS_RANGE[1];

    const [yearPreview, setYearPreview] = useState<[number, number]>(() => normalizeRange(filters.yearRange, yearMin, yearMax));
    const [pricePreview, setPricePreview] = useState<[number, number]>(() => normalizeRange(filters.priceRange, priceMin, priceMax));
    const [hoursPreview, setHoursPreview] = useState<[number, number]>(() => normalizeRange(filters.hours, hoursMin, hoursMax));

    const [yearMinText, setYearMinText] = useState(String(filters.yearRange[0]));
    const [yearMaxText, setYearMaxText] = useState(String(filters.yearRange[1]));
    const [priceMinText, setPriceMinText] = useState(String(filters.priceRange[0]));
    const [priceMaxText, setPriceMaxText] = useState(String(filters.priceRange[1]));
    const [hoursMinText, setHoursMinText] = useState(String(filters.hours[0]));
    const [hoursMaxText, setHoursMaxText] = useState(String(filters.hours[1]));

    const formatter = useMemo(() => new Intl.NumberFormat('pt-BR'), []);

    const categoryLookup = useMemo(() => {
        const lookup: Record<number, string> = {};

        for (const category of filterOptions.categories) {
            lookup[category.id] = category.name;
        }

        return lookup;
    }, [filterOptions.categories]);

    const stateLookup = useMemo(() => {
        const lookup: Record<string, string> = {};

        for (const state of filterOptions.states) {
            lookup[state.slug ?? state.id] = stateLabel(state.name, state.code);
        }

        return lookup;
    }, [filterOptions.states]);

    const conditionCounts = useMemo(() => {
        const source = filterOptions.conditions ?? {};
        const newCount = Number(source.new ?? 0);
        const usedCount =
            Number(source.used ?? 0) + Number(source.used_excellent ?? 0) + Number(source.used_with_marks ?? 0) + Number(source[''] ?? 0);

        return {
            new: newCount,
            used: usedCount,
        };
    }, [filterOptions.conditions]);

    const hasActiveFilters =
        filters.category.length > 0 ||
        filters.condition !== 'all' ||
        filters.location.length > 0 ||
        filters.yearRange[0] !== yearMin ||
        filters.yearRange[1] !== yearMax ||
        filters.priceRange[0] !== priceMin ||
        filters.priceRange[1] !== priceMax ||
        filters.hours[0] !== hoursMin ||
        filters.hours[1] !== hoursMax;

    useEffect(() => {
        const normalized = normalizeRange(filters.yearRange, yearMin, yearMax);
        setYearPreview(normalized);
        setYearMinText(String(normalized[0]));
        setYearMaxText(String(normalized[1]));
    }, [filters.yearRange, yearMax, yearMin]);

    useEffect(() => {
        const normalized = normalizeRange(filters.priceRange, priceMin, priceMax);
        setPricePreview(normalized);
        setPriceMinText(String(normalized[0]));
        setPriceMaxText(String(normalized[1]));
    }, [filters.priceRange, priceMax, priceMin]);

    useEffect(() => {
        const normalized = normalizeRange(filters.hours, hoursMin, hoursMax);
        setHoursPreview(normalized);
        setHoursMinText(String(normalized[0]));
        setHoursMaxText(String(normalized[1]));
    }, [filters.hours, hoursMax, hoursMin]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= MOBILE_BREAKPOINT) {
                setExpandedSections({ ...DESKTOP_DEFAULT_SECTIONS });
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSection = (section: SectionKey) => {
        setExpandedSections((previous) => ({
            ...previous,
            [section]: !previous[section],
        }));
    };

    const clearAllFilters = () => {
        onFiltersChange({
            category: [],
            condition: 'all',
            location: [],
            yearRange: [yearMin, yearMax],
            priceRange: [priceMin, priceMax],
            hours: [hoursMin, hoursMax],
        });
    };

    const removeFilter = (filter: AppliedFilter) => {
        const nextFilters = { ...filters };

        switch (filter.type) {
            case 'category':
                nextFilters.category = nextFilters.category.filter((category) => category !== String(filter.value));
                break;
            case 'condition':
                nextFilters.condition = 'all';
                break;
            case 'location':
                nextFilters.location = nextFilters.location.filter((state) => state !== String(filter.value));
                break;
        }

        onFiltersChange(nextFilters);
    };

    const appliedFilters = useMemo<AppliedFilter[]>(() => {
        const nextFilters: AppliedFilter[] = [];

        for (const categoryId of filters.category) {
            nextFilters.push({
                type: 'category',
                value: categoryId,
                display: categoryLookup[Number(categoryId)] || `Categoria ${categoryId}`,
            });
        }

        if (filters.condition !== 'all') {
            nextFilters.push({
                type: 'condition',
                value: filters.condition,
                display: filters.condition === 'new' ? 'Novos' : 'Usados',
            });
        }

        for (const slug of filters.location) {
            nextFilters.push({
                type: 'location',
                value: slug,
                display: stateLookup[slug] || `Estado ${slug}`,
            });
        }

        return nextFilters;
    }, [categoryLookup, filters.category, filters.condition, filters.location, stateLookup]);

    const applyYearRange = (range: [number, number], shouldCommit = false) => {
        const nextRange = normalizeRange(range, yearMin, yearMax);
        setYearPreview(nextRange);
        setYearMinText(String(nextRange[0]));
        setYearMaxText(String(nextRange[1]));

        if (shouldCommit) {
            onFiltersChange({ ...filters, yearRange: nextRange });
        }
    };

    const applyPriceRange = (range: [number, number], shouldCommit = false) => {
        const nextRange = normalizeRange(range, priceMin, priceMax);
        setPricePreview(nextRange);
        setPriceMinText(String(nextRange[0]));
        setPriceMaxText(String(nextRange[1]));

        if (shouldCommit) {
            onFiltersChange({ ...filters, priceRange: nextRange });
        }
    };

    const applyHoursRange = (range: [number, number], shouldCommit = false) => {
        const nextRange = normalizeRange(range, hoursMin, hoursMax);
        setHoursPreview(nextRange);
        setHoursMinText(String(nextRange[0]));
        setHoursMaxText(String(nextRange[1]));

        if (shouldCommit) {
            onFiltersChange({ ...filters, hours: nextRange });
        }
    };

    const commitYearBound = (bound: RangeBound) => {
        const rawValue = bound === 'min' ? yearMinText : yearMaxText;
        const parsed = parseNullableNumber(rawValue);
        let [nextMin, nextMax] = yearPreview;

        if (parsed !== null) {
            const value = clampNumber(Math.round(parsed), yearMin, yearMax);

            if (bound === 'min') {
                nextMin = value;
                nextMax = Math.max(value, nextMax);
            } else {
                nextMax = value;
                nextMin = Math.min(nextMin, value);
            }
        }

        applyYearRange([nextMin, nextMax], true);
    };

    const commitPriceBound = (bound: RangeBound) => {
        const rawValue = bound === 'min' ? priceMinText : priceMaxText;
        const parsed = parseNullableNumber(rawValue);
        let [nextMin, nextMax] = pricePreview;

        if (parsed !== null) {
            const value = clampNumber(Math.round(parsed), priceMin, priceMax);

            if (bound === 'min') {
                nextMin = value;
                nextMax = Math.max(value, nextMax);
            } else {
                nextMax = value;
                nextMin = Math.min(nextMin, value);
            }
        }

        applyPriceRange([nextMin, nextMax], true);
    };

    const commitHoursBound = (bound: RangeBound) => {
        const rawValue = bound === 'min' ? hoursMinText : hoursMaxText;
        const parsed = parseNullableNumber(rawValue);
        let [nextMin, nextMax] = hoursPreview;

        if (parsed !== null) {
            const value = clampNumber(Math.round(parsed), hoursMin, hoursMax);

            if (bound === 'min') {
                nextMin = value;
                nextMax = Math.max(value, nextMax);
            } else {
                nextMax = value;
                nextMin = Math.min(nextMin, value);
            }
        }

        applyHoursRange([nextMin, nextMax], true);
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, commit: () => void) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        commit();
        event.currentTarget.blur();
    };

    return (
        <div className={cn('rounded-xl bg-white p-3 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:p-6', className)}>
            <button
                type="button"
                onClick={() => setIsSidebarCollapsed((previous) => !previous)}
                className="flex w-full items-center justify-between py-2 text-left font-semibold text-gray-900 transition-colors hover:text-gray-700 lg:hidden"
            >
                <span>Filtros {appliedFilters.length > 0 && `(${appliedFilters.length})`}</span>
                {isSidebarCollapsed ? <ChevronDown aria-hidden="true" className="h-5 w-5" /> : <ChevronUp aria-hidden="true" className="h-5 w-5" />}
            </button>

            <div
                className={cn(
                    'space-y-6',
                    isSidebarCollapsed ? 'hidden lg:flex lg:h-full lg:min-h-0 lg:flex-col' : 'block lg:flex lg:h-full lg:min-h-0 lg:flex-col',
                )}
            >
                <div className="space-y-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1">
                    {appliedFilters.length > 0 && (
                        <section>
                            <h3 className="mb-3 text-sm font-semibold text-gray-900">Filtros aplicados</h3>

                            <div className="flex flex-wrap gap-2">
                                {appliedFilters.map((filter) => (
                                    <Badge
                                        key={`${filter.type}-${filter.value}`}
                                        className="border border-blue-800 bg-white text-blue-800 hover:bg-blue-50"
                                    >
                                        {filter.display}
                                        <button
                                            type="button"
                                            onClick={() => removeFilter(filter)}
                                            className="ml-2 rounded-sm hover:text-blue-900"
                                            aria-label={`Remover filtro ${filter.display}`}
                                        >
                                            <X aria-hidden="true" className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </section>
                    )}

                    <div className="space-y-4">
                        <FilterSection title="Categoria" expanded={expandedSections.category} onToggle={() => toggleSection('category')}>
                            <CategoryTree
                                categories={filterOptions.categories}
                                selectedCategories={filters.category}
                                onCategoryChange={(categoryId, checked) => {
                                    const nextCategories = checked
                                        ? Array.from(new Set([...filters.category, categoryId]))
                                        : filters.category.filter((category) => category !== categoryId);

                                    onFiltersChange({ ...filters, category: nextCategories });
                                }}
                            />
                        </FilterSection>

                        <FilterSection title="Novo/usado" expanded={expandedSections.condition} onToggle={() => toggleSection('condition')}>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={filters.condition === 'new' ? 'default' : 'outline'}
                                    aria-pressed={filters.condition === 'new'}
                                    onClick={() =>
                                        onFiltersChange({
                                            ...filters,
                                            condition: filters.condition === 'new' ? 'all' : 'new',
                                        })
                                    }
                                    className="px-2"
                                >
                                    <span className="truncate">Novos</span>
                                    <span className="ml-1 text-xs tabular-nums">({conditionCounts.new})</span>
                                </Button>

                                <Button
                                    type="button"
                                    size="sm"
                                    variant={filters.condition === 'used' ? 'default' : 'outline'}
                                    aria-pressed={filters.condition === 'used'}
                                    onClick={() =>
                                        onFiltersChange({
                                            ...filters,
                                            condition: filters.condition === 'used' ? 'all' : 'used',
                                        })
                                    }
                                    className="px-2"
                                >
                                    <span className="truncate">Usados</span>
                                    <span className="ml-1 text-xs tabular-nums">({conditionCounts.used})</span>
                                </Button>
                            </div>
                        </FilterSection>

                        <FilterSection title="Localização" expanded={expandedSections.location} onToggle={() => toggleSection('location')}>
                            <div className="space-y-2">
                                {filterOptions.states.map((state) => {
                                    const slug = state.slug ?? String(state.id);
                                    const isChecked = filters.location.includes(slug);
                                    const isDisabled = state.count === 0 && !isChecked;

                                    return (
                                        <label
                                            key={state.id}
                                            className={cn('flex items-center gap-2', isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                                        >
                                            <Checkbox
                                                checked={isChecked}
                                                disabled={isDisabled}
                                                onCheckedChange={(checked) => {
                                                    const shouldAdd = checked === true;
                                                    const nextLocations = shouldAdd
                                                        ? [...filters.location, slug]
                                                        : filters.location.filter((locationSlug) => locationSlug !== slug);

                                                    onFiltersChange({
                                                        ...filters,
                                                        location: nextLocations,
                                                    });
                                                }}
                                            />

                                            <span className="text-sm text-gray-700">
                                                {stateLabel(state.name, state.code)}
                                                {state.count !== undefined && (
                                                    <span className="ml-1 text-gray-500 tabular-nums">({state.count})</span>
                                                )}
                                            </span>
                                        </label>
                                    );
                                })}

                                {filterOptions.states.length === 0 && <p className="text-sm text-gray-500 italic">Nenhum estado disponível</p>}
                            </div>
                        </FilterSection>

                        <FilterSection title="Ano" expanded={expandedSections.year} onToggle={() => toggleSection('year')}>
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 tabular-nums">
                                {yearPreview[0]} - {yearPreview[1]}
                            </div>

                            <Slider
                                id="year-range-slider"
                                aria-label="Intervalo de ano"
                                min={yearMin}
                                max={yearMax}
                                step={1}
                                value={yearPreview}
                                onValueChange={(value) => applyYearRange(toRangeValue(value, yearPreview))}
                                onValueCommit={(value) => applyYearRange(toRangeValue(value, yearPreview), true)}
                                className="py-2"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="year-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="year-min-input"
                                        name="year_min"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={yearMinText}
                                        onChange={(event) => setYearMinText(event.target.value)}
                                        onBlur={() => commitYearBound('min')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitYearBound('min'))}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="year-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="year-max-input"
                                        name="year_max"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={yearMaxText}
                                        onChange={(event) => setYearMaxText(event.target.value)}
                                        onBlur={() => commitYearBound('max')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitYearBound('max'))}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title="Preço" expanded={expandedSections.price} onToggle={() => toggleSection('price')}>
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 tabular-nums">
                                R$ {formatter.format(pricePreview[0])} - R$ {formatter.format(pricePreview[1])}
                            </div>

                            <Slider
                                id="price-range-slider"
                                aria-label="Intervalo de preço"
                                min={priceMin}
                                max={priceMax}
                                step={1000}
                                value={pricePreview}
                                onValueChange={(value) => applyPriceRange(toRangeValue(value, pricePreview))}
                                onValueCommit={(value) => applyPriceRange(toRangeValue(value, pricePreview), true)}
                                className="py-2"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="price-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="price-min-input"
                                        name="price_min"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={priceMinText}
                                        onChange={(event) => setPriceMinText(event.target.value)}
                                        onBlur={() => commitPriceBound('min')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitPriceBound('min'))}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="price-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="price-max-input"
                                        name="price_max"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={priceMaxText}
                                        onChange={(event) => setPriceMaxText(event.target.value)}
                                        onBlur={() => commitPriceBound('max')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitPriceBound('max'))}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title="Horas de uso" expanded={expandedSections.hours} onToggle={() => toggleSection('hours')}>
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 tabular-nums">
                                {formatter.format(hoursPreview[0])}h - {formatter.format(hoursPreview[1])}h
                            </div>

                            <Slider
                                id="hours-range-slider"
                                aria-label="Intervalo de horas de uso"
                                min={hoursMin}
                                max={hoursMax}
                                step={100}
                                value={hoursPreview}
                                onValueChange={(value) => applyHoursRange(toRangeValue(value, hoursPreview))}
                                onValueCommit={(value) => applyHoursRange(toRangeValue(value, hoursPreview), true)}
                                className="py-2"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="hours-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="hours-min-input"
                                        name="sailing_time_min"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={hoursMinText}
                                        onChange={(event) => setHoursMinText(event.target.value)}
                                        onBlur={() => commitHoursBound('min')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitHoursBound('min'))}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="hours-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="hours-max-input"
                                        name="sailing_time_max"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={hoursMaxText}
                                        onChange={(event) => setHoursMaxText(event.target.value)}
                                        onBlur={() => commitHoursBound('max')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitHoursBound('max'))}
                                    />
                                </div>
                            </div>
                        </FilterSection>
                    </div>
                </div>

                <div className="mt-4 flex gap-2 border-t border-gray-200 pt-4">
                    <Button type="button" variant="outline" className="flex-1" onClick={clearAllFilters} disabled={!hasActiveFilters}>
                        Limpar filtros
                    </Button>
                    <Button type="button" onClick={onSaveSearch} className="flex-1 bg-green-600 hover:bg-green-700">
                        Salvar pesquisa
                    </Button>
                </div>
            </div>
        </div>
    );
};
